import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DropdownItem } from '@base-app';
import { CardGrid, NoData } from '@builder';
import { COMMON_TRANSLATION } from '@constants/index';
import { FourYearSequenceGoalCardProps } from './FourYearSequenceGoalCard.types';
import { getDateWithoutTZ } from '@common/utils';
import { HeaderOption } from '../../../CollectionAndSequencing.types';
import { isUserAdministrator } from '@modules/auth/utils/userRole';
import { STUDY_INSIGHTS_TRANSLATION, colors } from '@modules/study-insights/constants';
import { StyledSequenceGoal, StyledSequenceGoalCard } from './FourYearSequenceGoalCard.styles';
import { useAuthStore } from '@modules/auth/store';

const FourYearSequenceGoalCard: FC<FourYearSequenceGoalCardProps> = ({ data, handleEdit }) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { user: currentUser } = useAuthStore();
  const [percentage, setPercentage] = useState<number>(0);

  const getHeaderOptions = (): DropdownItem[] | [] => {
    if (currentUser == null || isUserAdministrator(currentUser)) {
      return [
        {
          id: HeaderOption.EDIT,
          label: t('header.options.edit'),
        },
      ];
    } else {
      return [];
    }
  };

  const handleOptionClick = (options: DropdownItem): void => {
    if (options.id === HeaderOption.EDIT) {
      handleEdit();
    }
  };

  useEffect(() => {
    if (data?.value) {
      const percentage = (100 * data?.value) / data?.target;

      setPercentage(percentage);
    }
  }, [data]);

  return (
    <CardGrid
      css={{ width: '100%' }}
      handleClick={(e) => handleOptionClick(e)}
      headerOptions={getHeaderOptions()}
      title={t('four_year_sequence_goal.title')}
    >
      <StyledSequenceGoalCard>
        {data ? (
          <StyledSequenceGoal
            accentColor={colors.yellow300}
            id="4year-sequence-goal"
            date={`${getDateWithoutTZ(data.startDate)} - ${getDateWithoutTZ(data.endDate)}`}
            dateLabel={t('four_year_sequence_goal.study_time_range')}
            percentage={percentage}
            target={data.target}
            targetLabel={t('four_year_sequence_goal.study_lifetime_goal')}
            value={data.value}
          />
        ) : (
          <NoData message={t('no_data_available', { ns: COMMON_TRANSLATION })} />
        )}
      </StyledSequenceGoalCard>
    </CardGrid>
  );
};

export default FourYearSequenceGoalCard;
